import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { BiX } from "react-icons/bi";
import useDarkMode from "use-dark-mode";
import Loader from "../Loader";
import Tooltip from "../Tooltip";
import styles from "./DropdownSearch.module.sass";

const DropdownSearch = ({
  className,
  classDropdownHead,
  classDropdownLabel,
  value,
  setValue,
  options,
  isBackgroundWhite,
  label,
  tooltip,
  small,
  upBody,
  isLoading,
  isDelete,
  onSearch,
  character,
  timeoutSearch = 500,
  placeholder = "Select...",
}) => {
  const [visible, setVisible] = useState(false);
  const [bodyStyle, setBodyStyle] = useState({});
  const [searchText, setSearchText] = useState("");
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const headRef = useRef(null);
  const searchInputRef = useRef(null);
  const bodyRef = useRef(null);
  const darkMode = useDarkMode(false);

  // Outside click: incluir el portal (createPortal) porque OutsideClickHandler no lo detecta
  useEffect(() => {
    if (!visible) return;
    const handleDocumentClick = (e) => {
      const target = e.target;
      const isInsideDropdown = dropdownRef.current?.contains(target);
      const isInsidePortal = bodyRef.current?.contains(target);
      if (!isInsideDropdown && !isInsidePortal) {
        setVisible(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [visible]);

  // Recalcula la posición del portal al abrir y al hacer scroll/resize
  useEffect(() => {
    if (!visible) return;

    const updatePosition = () => {
      if (!headRef.current) return;
      const rect = headRef.current.getBoundingClientRect();
      setBodyStyle({
        position: "fixed",
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        zIndex: 99999,
      });
    };

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [visible]);

  // Limpiar búsqueda al cerrar
  useEffect(() => {
    if (!visible) {
      setSearchText("");
    } else {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [visible]);

  const handleClick = (value) => {
    setValue(value);
    setVisible(false);
  };

  const handleSearch = (ev) => {
    const val = ev.target.value;
    setSearchText(val);
    if (onSearch) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onSearch(val);
      }, timeoutSearch);
    }
  };

  const filteredOptions = searchText
    ? options.filter((op) =>
        op.label?.toLowerCase().includes(searchText.toLowerCase())
      )
    : options;

  const selectedDisplayValue = typeof value === "object" && value !== null ? value?.label : value;

  const bodyContent = (
    <div
      ref={bodyRef}
      className={cn(styles.body, { [styles.bodyUp]: upBody })}
      style={{
        ...bodyStyle,
        visibility: visible ? "visible" : "hidden",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(3px)",
      }}
    >
      {isLoading ? (
        <div className={cn(styles.loaderContainer)}>
          <Loader />
        </div>
      ) : filteredOptions.length === 0 ? (
        <div className={cn(styles.loaderContainer)}>No results</div>
      ) : (
        <>
          {visible &&
            filteredOptions.map((op, index) => (
              <div
                className={cn(styles.option, {
                  [styles.selectioned]: (typeof value === "object" && value !== null ? op.value === value?.value : op.value === value) || op === value,
                })}
                onClick={() => handleClick(op, index)}
                key={index}
              >
                {op.character && <div className={cn(styles.character)}>{op.character}</div>}
                {op.label}
              </div>
            ))}
        </>
      )}
    </div>
  );

  return (
    <>
      {label && (
        <div className={cn(styles.label, classDropdownLabel)}>
          {label}{" "}
          {tooltip && (
            <Tooltip className={styles.tooltip} title={tooltip} icon="info" place="right" />
          )}
        </div>
      )}
      <div
        ref={dropdownRef}
        style={{ zIndex: 999 }}
        className={cn(
          styles.dropdown,
          className,
          { [styles.small]: small },
          { [styles.active]: visible }
        )}
      >
        <div
          ref={headRef}
          style={
            isBackgroundWhite && !darkMode.value
              ? { background: "white", display: "flex", alignItems: "center", width: "100%" }
              : { display: "flex", alignItems: "center", width: "100%" }
          }
          className={cn(styles.head, classDropdownHead)}
          onClick={() => setVisible(!visible)}
        >
          <div className={styles.selection}>
            {!visible && (selectedDisplayValue || placeholder)}
            {visible && (
              <input
                ref={searchInputRef}
                className={styles.inputSearch}
                value={searchText}
                onChange={handleSearch}
                placeholder={selectedDisplayValue || placeholder}
                onClick={(e) => e.stopPropagation()}
                autoComplete="off"
              />
            )}
          </div>
          {isDelete && (
            <BiX
              style={{ fontSize: "16px", position: "absolute", right: -20, cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                setValue("");
                setSearchText("");
              }}
            />
          )}
        </div>

        {ReactDOM.createPortal(bodyContent, document.body)}
      </div>
    </>
  );
};

export default DropdownSearch;
